import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { ENTITY_TYPES, VERSION_TARGETS } from 'common';
import MODAL_TYPES from 'constants/modals';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { sortableHandle } from 'react-sortable-hoc';
import { useUpdateLearningCommunitiesBookmarkMutation } from 'store/services/learningCommunities';
import {
  useEditLearningCommunityChapterNameMutation,
  useRemoveLearningCommunityChapterMutation,
} from 'store/services/learningCommunities/volumeChapter';
import { showModal } from 'store/slices/modal';
import Button from 'style-guide/Button';
import Form from 'style-guide/Form';
import { Trash } from 'style-guide/Icons';
import { TextArea } from 'style-guide/Input';
import { message } from 'style-guide/ToastMessages';
import Text from 'style-guide/Typography/Text';
import Title from 'style-guide/Typography/Title';
import { object, string } from 'yup';

const newChapterNameSchema = object({
  newChapterName: string()
    .trim()
    .min(3, 'Chapter name must contain at least 3 characters')
    .max(180, 'Chapter name must contain max 180 characters')
    .required(),
}).required();

const { LEARNING_COMMUNITY, MEMBER } = ENTITY_TYPES;
const { STORY } = VERSION_TARGETS;

const ChapterDragHandle = sortableHandle(
  ({ isLast, name, chapterId, volumeId, isCheckedChapter, showChapter, isPreview }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { id, urlName, category, role, chapterId: activeChapterId } = useParams();

    const isActiveChapter = chapterId === +activeChapterId;

    const [removeChapter] = useRemoveLearningCommunityChapterMutation();
    const [editChapter] = useEditLearningCommunityChapterNameMutation();
    const [updateBookmark] = useUpdateLearningCommunitiesBookmarkMutation();

    const {
      handleSubmit,
      control,
      getValues,
      formState: { isValid, errors },
    } = useForm({
      mode: 'onChange',
      defaultValues: { newChapterName: name },
      resolver: yupResolver(newChapterNameSchema),
    });

    const errorMessage = errors?.newChapterName?.message;

    const handleRequest = (formData) => {
      if (formData.newChapterName !== name) editChapter({ volumeId, chapterId, id, ...formData });
    };

    const chapterMenuUrl = `/${LEARNING_COMMUNITY.key}/${id}/${urlName}/${role}/${chapterId}/${
      isCheckedChapter ? category : STORY.value
    }`;

    const handleErrorMessage = () => message.error(errorMessage);

    const isMember = role === MEMBER.short;

    const onRemove = () => {
      dispatch(
        showModal({
          type: MODAL_TYPES.CONFIRMATION,
          params: {
            modal: {
              title: 'Confirmation',
            },
            body: {
              title: <Title variant={5}>Are you sure you want to delete?</Title>,
              onOk: () => removeChapter({ id, chapterId, volumeId }),
            },
          },
        })
      );
    };

    const formValue = getValues('newChapterName');

    const handleBlur = isValid ? handleSubmit(handleRequest) : handleErrorMessage;

    return (
      <div
        className={classNames({ pointer: !isCheckedChapter }, 'chapter-textarea d-flex justify-content-between pl--16')}
        onClick={() => {
          const chapterBookmarkData = { courseId: id, chapterId: +activeChapterId, volumeId, section: category };
          updateBookmark(chapterBookmarkData);
          if (showChapter) {
            navigate(chapterMenuUrl);
          }
        }}
      >
        {!isMember && isCheckedChapter && !isPreview ? (
          <Form onBlur={handleBlur}>
            <Controller
              name='newChapterName'
              control={control}
              render={({ field }) => (
                <TextArea
                  placeholder=''
                  autoSize={{ maxRows: 5 }}
                  name='newChapterName'
                  readOnly={!isCheckedChapter || isMember}
                  autoComplete='off'
                  {...field}
                />
              )}
            />
          </Form>
        ) : (
          <Text>{formValue}</Text>
        )}

        {!isLast && !isActiveChapter && !isMember ? (
          <Button
            className='trash-icon pv--0'
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          >
            <Trash width={12} height={12} />
          </Button>
        ) : null}
      </div>
    );
  }
);

export default ChapterDragHandle;
